import { useMemo } from "react";
import { useAuth } from "../../../lib/hooks/use-auth";
import { useNavigationCommands } from "./commands/navigation";
import { useCreateCommands } from "./commands/create";
import { usePromptCommands } from "./commands/prompts";
import { useActionCommands } from "./commands/actions";
import { useSystemCommands } from "./commands/system";
import { useWorkforceCommands } from "./commands/workforce";
import { useRoutineCommands } from "./commands/routines";
import { loadRecency } from "./commands/recent";
import { rankCommands } from "./rank";
import type { Command, CommandContext } from "./types";

/**
 * Composes every CONSOLE command source into one ranked index (S3.08 + S3.16).
 * We run our own scoring (cmdk renders with `shouldFilter={false}`).
 *
 * №44 — **the scoring itself moved to `rank.ts`**, unchanged, because the App
 * Shell now has a palette too and the two must share one ranking. What stays
 * here is the thing that is genuinely console-specific: WHICH SOURCES compose
 * the index. `fuzzyScore` is re-exported so existing importers are unaffected.
 */

export { fuzzyScore } from "./rank";

export interface CommandIndex {
  /** Full unranked set — feeds the cheatsheet and recents lookup. */
  all: Command[];
  /** Ranked, filtered, capped results for the current query (empty when blank). */
  results: Command[];
}

export function useCommandIndex(query: string, ctx: CommandContext): CommandIndex {
  const nav = useNavigationCommands(ctx);
  const create = useCreateCommands(ctx);
  const prompt = usePromptCommands(ctx);
  const action = useActionCommands(ctx);
  const system = useSystemCommands(ctx);
  // Sprint 23 — themed Hire + Open/Filter/Sort Workforce commands.
  const workforce = useWorkforceCommands(ctx);
  // Sprint 26.1 — "Run routine…" jump into the agent's Routines tab.
  const routines = useRoutineCommands(ctx);
  const { currentWorkspaceId } = useAuth();

  const all = useMemo(
    () => [...nav, ...create, ...prompt, ...action, ...system, ...workforce, ...routines],
    [nav, create, prompt, action, system, workforce, routines],
  );

  const results = useMemo(
    () => rankCommands(query, all, loadRecency(currentWorkspaceId)),
    [query, all, currentWorkspaceId],
  );

  return { all, results };
}
